// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useDeviceChat } from "../../../Api/newSockets.api";

type Props = {
  deviceId: number;
  deviceName?: string;
  /** When true, fill the parent container's height (widget) instead of the full-page viewport. */
  fill?: boolean;
  /** Hide the internal header (the widget renders its own). */
  hideHeader?: boolean;
};

/**
 * Chat UI for the per-device agent. Streams the assistant reply, renders tool activity
 * inline, and shows an Approve/Deny card whenever the agent proposes a control action
 * (run program, capture, change settings, …) — nothing touches hardware without consent.
 */
const ChatInterface = ({ deviceId, deviceName, fill, hideHeader }: Props) => {
  const { messages, sendMessage, busy, error, pendingConfirms, resolveConfirms, reset } =
    useDeviceChat(deviceId);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pendingConfirms]);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    sendMessage(text);
    setInput("");
  };

  const approve = (approveAll: boolean) => {
    const decisions: Record<string, boolean> = {};
    pendingConfirms.forEach((c) => { decisions[c.toolUseId] = approveAll; });
    resolveConfirms(decisions);
  };

  const suggestions = [
    "Summarize this device's recent metrics and status.",
    "Is anything wrong with this device right now?",
    "What programs can I run here?",
  ];

  return (
    <div style={{ ...S.wrap, ...(fill ? { height: "100%", minHeight: 0 } : {}) }}>
      {!hideHeader && (
        <div style={S.header}>
          <div>
            <div style={S.title}>🤖 Device Chat{deviceName ? ` — ${deviceName}` : ""}</div>
            <div style={S.subtitle}>Ask about state, diagnose issues, or request actions (control needs your approval).</div>
          </div>
          <button style={S.resetBtn} onClick={reset} title="Clear conversation">Reset</button>
        </div>
      )}

      <div ref={scrollRef} style={S.messages}>
        {messages.length === 0 && (
          <div style={S.empty}>
            <div style={{ marginBottom: 12, color: "#8a9199" }}>Start a conversation with this device.</div>
            {suggestions.map((s) => (
              <button key={s} style={S.suggestion} onClick={() => sendMessage(s)} disabled={busy}>{s}</button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{ ...S.row, justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ ...S.bubble, ...(m.role === "user" ? S.userBubble : S.assistantBubble) }}>
              {m.content && <div style={S.msgText}>{m.content}{m.streaming && <span style={S.caret}>▋</span>}</div>}
              {(m.tools || []).map((t, j) => (
                <div key={j} style={S.toolChip}>
                  {t.state === "ran" && "✓ "}
                  {t.state === "failed" && "✗ "}
                  {t.state === "denied" && "⊘ "}
                  <code style={{ color: "#c7d0d9" }}>{t.name}</code>
                  {t.state === "denied" && " (denied)"}
                  {t.state === "failed" && " (failed)"}
                </div>
              ))}
            </div>
          </div>
        ))}

        {pendingConfirms.length > 0 && (
          <div style={S.confirmCard}>
            <div style={S.confirmTitle}>⚠ The agent wants to run {pendingConfirms.length > 1 ? "these actions" : "this action"} on the device:</div>
            {pendingConfirms.map((c) => (
              <div key={c.toolUseId} style={S.confirmItem}>
                <code style={{ color: "#ffd479" }}>{c.name}</code> — {c.summary}
              </div>
            ))}
            <div style={S.confirmActions}>
              <button style={S.approveBtn} onClick={() => approve(true)}>Approve</button>
              <button style={S.denyBtn} onClick={() => approve(false)}>Deny</button>
            </div>
          </div>
        )}

        {error && <div style={S.error}>⚠ {error}</div>}
      </div>

      <form style={S.inputRow} onSubmit={submit}>
        <input
          style={S.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={busy ? "Agent is working…" : "Ask this device anything…"}
          disabled={busy && pendingConfirms.length === 0}
        />
        <button style={S.sendBtn} type="submit" disabled={busy || !input.trim()}>
          {busy ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", height: "calc(100vh - 220px)", minHeight: 420, background: "#12161a", border: "1px solid #232a31", borderRadius: 12, overflow: "hidden" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #232a31", background: "linear-gradient(135deg,#151a1f,#1a2026)" },
  title: { color: "#e6edf3", fontWeight: 600, fontSize: "1rem" },
  subtitle: { color: "#8a9199", fontSize: "0.78rem", marginTop: 2 },
  resetBtn: { background: "transparent", color: "#8a9199", border: "1px solid #2c343c", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: "0.8rem" },
  messages: { flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 12 },
  empty: { margin: "auto", textAlign: "center", display: "flex", flexDirection: "column", gap: 8, maxWidth: 460 },
  suggestion: { background: "#1a2026", color: "#c7d0d9", border: "1px solid #2c343c", borderRadius: 8, padding: "10px 14px", cursor: "pointer", fontSize: "0.85rem", textAlign: "left" },
  row: { display: "flex" },
  bubble: { maxWidth: "82%", borderRadius: 12, padding: "10px 14px", fontSize: "0.9rem", lineHeight: 1.5 },
  userBubble: { background: "#2563eb", color: "#fff", borderBottomRightRadius: 4 },
  assistantBubble: { background: "#1a2026", color: "#e6edf3", border: "1px solid #232a31", borderBottomLeftRadius: 4 },
  msgText: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
  caret: { opacity: 0.6 },
  toolChip: { marginTop: 8, fontSize: "0.75rem", color: "#9aa4ad", background: "#12171c", border: "1px solid #232a31", borderRadius: 6, padding: "4px 8px", display: "inline-block" },
  confirmCard: { background: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.4)", borderRadius: 10, padding: 14 },
  confirmTitle: { color: "#ffd479", fontWeight: 600, fontSize: "0.85rem", marginBottom: 8 },
  confirmItem: { color: "#e6edf3", fontSize: "0.85rem", marginBottom: 6 },
  confirmActions: { display: "flex", gap: 10, marginTop: 10 },
  approveBtn: { background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600 },
  denyBtn: { background: "transparent", color: "#f87171", border: "1px solid #f87171", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 600 },
  error: { color: "#f87171", fontSize: "0.85rem", padding: "8px 12px", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8 },
  inputRow: { display: "flex", gap: 10, padding: 14, borderTop: "1px solid #232a31", background: "#12161a" },
  input: { flex: 1, background: "#0d1114", color: "#e6edf3", border: "1px solid #2c343c", borderRadius: 8, padding: "12px 14px", fontSize: "0.9rem", outline: "none" },
  sendBtn: { background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "12px 22px", cursor: "pointer", fontWeight: 600 },
};

export default ChatInterface;
